import { BottomBar, BottomBarActions } from '@monorepo/base/src/components/bottom-bar/bottom-bar';
import { PrimaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { Disable } from '@monorepo/base/src/components/disable/disable';
import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { DispatchLogsTypes, useLogs } from '@monorepo/controlled/src/hooks/use-logs';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { useNavigateParams } from '@monorepo/tools/src/lib/hooks/tools/use-navigate-params';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BidderApi, ITrafficEntityError, ITrafficEntityFields } from '../../../../modules/apis/bidder.api';
import {
	missingTrafficFields,
	splitTrafficErrors,
	TrafficForm,
} from '../../../../modules/components/forms/bidder-traffic/bidder-traffic.form';
import { useList } from '../../../../modules/hooks/tools/use-list';
import { TrafficEntityModel } from '../../../../modules/models/traffic-entity.model';
import { IAdminxStore } from '../../../../modules/stores';
import routes from '../../../../modules/routes/index.routes';

export const EditTraffic = observer(() => {
	const { id } = useParams();
	const { bidderStore } = useStores<IAdminxStore>();
	const trafficList = bidderStore.trafficList;
	const { dispatchLog } = useLogs();
	const navigate = useNavigateParams();
	const [fields, setFields] = useState<ITrafficEntityFields | null>(null);
	const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof ITrafficEntityFields, string>>>({});
	const [isSaving, setIsSaving] = useState(false);
	const loadedId = useRef<string | undefined>(undefined);

	useList<TrafficEntityModel>(trafficList);

	useEffect(() => {
		bidderStore.getInputOptions().fetch();
	}, []);

	const entity = trafficList.getList().find(row => String(row.id) === id);

	useEffect(() => {
		if (!entity || !id || loadedId.current === id) {
			return;
		}
		loadedId.current = id;
		setFields({
			geo: entity.geo,
			op_system: entity.op_system,
			browser: entity.browser,
			device_type: entity.device_type,
		});
	}, [entity, id]);

	const onSave = () => {
		if (!fields || !id) {
			return;
		}
		const missing = missingTrafficFields(fields);
		if (Object.keys(missing).length) {
			setFieldErrors(missing);
			return;
		}

		setIsSaving(true);
		BidderApi.updateTrafficEntity({ ...fields, id: Number(id) })
			.then(() => {
				navigate(routes.bidder.traffic());
				dispatchLog({
					msg: `Traffic entity ${id} was successfully updated`,
					type: DispatchLogsTypes.Success,
				});
			})
			.catch((error: { data?: { errors?: ITrafficEntityError[] } }) => {
				const { fieldErrors: nextFieldErrors, messages } = splitTrafficErrors(error.data?.errors);
				setFieldErrors(nextFieldErrors);
				if (messages.length) {
					messages.forEach(msg => dispatchLog({ msg, type: DispatchLogsTypes.Error }));
				} else if (!error.data?.errors?.length) {
					dispatchLog({ msg: 'Failed to update traffic entity', type: DispatchLogsTypes.Error });
				}
			})
			.finally(() => {
				setIsSaving(false);
			});
	};

	return (
		<Disable is={isSaving || trafficList.getIsLoading()}>
			<Page>
				<Pagebar>
					<PagebarTitle>Edit Traffic</PagebarTitle>
				</Pagebar>
				{fields ? (
					<TrafficForm
						fields={fields}
						onChange={setFields}
						fieldErrors={fieldErrors}
						onClearError={field => setFieldErrors(current => ({ ...current, [field]: undefined }))}
						relatedCampaigns={entity?.rel_camp ?? []}
					/>
				) : !trafficList.getIsLoading() ? (
					<div>There is no traffic entity with id: {id}</div>
				) : null}
			</Page>
			<BottomBar>
				<BottomBarActions>
					<PrimaryButton
						onClick={onSave}
						disabled={!fields}
						isRotateIcon={isSaving}
						icon={isSaving ? 'loading-01' : 'send-03'}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'save_traffic_button')] }}>
						Save
					</PrimaryButton>
				</BottomBarActions>
			</BottomBar>
		</Disable>
	);
});
